"use client";

import { useIsDesktop } from "@/hooks/layout/use-is-desktop";
import { useHomeSidebarStore } from "@/stores/sidebar-store";
import { withPreventDefault } from "@/utils/with-prevent-default.ts";
import { withStopPropagation } from "@/utils/with-stop-propagation";
import { ArrowLeftIcon, ArrowRightIcon, CheckCheckIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import React from "react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  OnboardingPlaces,
  OnboardingStep,
  useOnboardingStore,
} from "./onboarding-provider";

const preventDefaultAndStopPropagation =
  withStopPropagation(withPreventDefault());

interface Props {
  readonly targetId: OnboardingStep;
  readonly content: string;
  readonly nextRoute?: string;
  readonly desktopOnly?: boolean;
  readonly canGoBack?: boolean;
}

export function OnboardingItem(props: React.PropsWithChildren<Props>) {
  const router = useRouter();
  const onboarding = useOnboardingStore((state) => state);
  const isDesktop = useIsDesktop();
  const isHomeSidebarOpen = useHomeSidebarStore((state) => state.isOpen);

  const canGoBack = props.canGoBack ?? true;
  const isLastItem = onboarding.onboardingSteps.length === 1;
  const isSidebarItem = props.targetId.includes(OnboardingPlaces.userSidebar);

  const onboardingStep =
    !isDesktop && props.desktopOnly ? undefined : props.targetId;

  if (
    (!isDesktop && props.desktopOnly) ||
    onboarding.currentStep !== props.targetId ||
    onboarding.readSteps[props.targetId] ||
    // On Mobile, hide sidebar item when home sidebar is closed and vice versa
    (!isDesktop && isSidebarItem && !isHomeSidebarOpen) ||
    (!isDesktop && !isSidebarItem && isHomeSidebarOpen)
  ) {
    return <div data-onboarding-step={onboardingStep}>{props.children}</div>;
  }

  const onNextClick = () => {
    onboarding.next?.();

    if (props.nextRoute) {
      router.push(props.nextRoute);
    }
  };

  return (
    <React.Fragment>
      <div className="fixed inset-0 z-40 bg-background/80"></div>

      <Popover open>
        <PopoverTrigger asChild>
          <div className="z-50" data-onboarding-step={onboardingStep}>
            {props.children}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="z-50 flex flex-col gap-0 p-2 pb-1 pt-2"
          sideOffset={8}
          side="right"
          onMouseOver={preventDefaultAndStopPropagation}
          // layoutId={props.targetId}
        >
          {props.content}
          <div className="flex justify-between">
            <Button
              size="icon"
              variant="ghost"
              onClick={onboarding.prev}
              className={!canGoBack ? "invisible" : undefined}
            >
              <ArrowLeftIcon className="size-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={onNextClick}>
              {isLastItem ? (
                <CheckCheckIcon className="size-4" />
              ) : (
                <ArrowRightIcon className="size-4" />
              )}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </React.Fragment>
  );
}
